import React, { useCallback, useMemo } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { useTouchableTooltipCollection } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';
import { TableGrid } from 'apps/sigma2-ui/src/explore-section/components/TableGrid';
import { HeatmapViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { callGenerateShareLink } from 'apps/sigma2-ui/src/state/generate-share-link';
import { TableGridTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableGridTooltip';
import { getColorForValue } from 'libs/end-to-end/src/heatmap-analysis';
import { HeatmapTableCell } from 'libs/end-to-end/src/heatmap-analysis';
import { getMetricByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/me';
import { useFocusArea } from 'apps/sigma2-ui/src/lib/hooks/useFocusArea';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { TableGridColumnHeaderTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableGridColumnHeaderTooltip';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';

const formatNumberFn = (value: number | null) =>
  value !== null
    ? `${parseFloat(
        formatNumberForDisplayInUnits({
          value,
          units: 'percentage',
          isPercentageAlreadyMultipliedBy100: false,
        }),
      ).toFixed(0)}%`
    : null;

const getCellKey = (rowIndex: number, columnIndex: number) =>
  `${String(rowIndex)}-${String(columnIndex)}`;

const buildTooltipItem = ({
  groupByLevel,
  metricInfo,
  impactValueNumber,
  confidenceValue,
  cellValue,
  sampleSize,
}: {
  groupByLevel: string;
  metricInfo: { focusArea: FocusArea; metric: MyMetricDefinition };
  impactValueNumber: number;
  confidenceValue: number;
  cellValue: number;
  sampleSize: number;
}) => ({
  type: 'heatmap-cell' as const,
  group: groupByLevel,
  focusArea: metricInfo.focusArea,
  metric: metricInfo.metric.columnHeaderValue,
  metricLabel: metricInfo.metric.displayName,
  impactScore: impactValueNumber,
  confidenceLevel: confidenceValue,
  disparityValue: cellValue,
  sampleSize,
});

const buildFirstColumnCell = (
  rowIndex: number,
  columnIndex: number,
  groupByLevel: string,
) => ({
  key: getCellKey(rowIndex, columnIndex),
  content: groupByLevel,
  className: getColorForValue(NaN),
  tooltipItem: null,
});

export const HeatmapViewMode = (props: HeatmapViewModeArguments) => {
  const { data: compatibleData, viewConfiguration } = props;

  const context = useCustomViz();
  const { lastSuccessfulCustomVizRequest } = context;

  const { myObservation } = useObservation();

  const { getFocusAreaByMetric } = useFocusArea();
  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();
  const { getColumnDisplayName } = useGetColumnDisplayName();

  const showNewExplanations = useMemo(
    () => myObservation?.showNewExplanations ?? false,
    [myObservation?.showNewExplanations],
  );

  const getSampleSizeFromColumnName = useCallback(
    ({
      columnIndex,
      rowIndex,
    }: {
      columnIndex: number;
      rowIndex: number;
    }): number | null => {
      if (!compatibleData) {
        return null;
      }

      return compatibleData.sampleSizes[rowIndex][columnIndex];
    },
    [compatibleData],
  );

  const headerVar = useMemo(() => {
    if (!compatibleData) return [];
    return compatibleData.columnHeaders.map((header) => ({
      key: header,
      label: getColumnDisplayName(header),
    }));
  }, [compatibleData, getColumnDisplayName]);

  const buildCellData = useCallback(
    ({
      cell,
      columnIndex,
      rowIndex,
    }: {
      cell: string;
      columnIndex: number;
      rowIndex: number;
    }) => {
      if (!compatibleData) return null;

      const rowMeta = compatibleData.rowsMeta[rowIndex];
      const groupByLevel = rowMeta?.groupByLevel ?? '';

      // First column: group label only
      if (columnIndex === 0) {
        return buildFirstColumnCell(rowIndex, columnIndex, groupByLevel);
      }

      // Data columns
      const impactValue = compatibleData.impactValues[rowIndex][columnIndex];
      const impactValueNumber = parseFloat(impactValue);
      const headerInfo = headerVar[columnIndex - 1];
      const metricInfo = headerInfo
        ? getMetricAndFocusAreaFromColumnName({ columnName: headerInfo.key })
        : null;

      return {
        key: getCellKey(rowIndex, columnIndex),
        content: cell ? (formatNumberFn(parseFloat(cell)) ?? 'N/A') : 'N/A',
        className: getColorForValue(impactValueNumber),
        tooltipItem: metricInfo
          ? buildTooltipItem({
              groupByLevel,
              metricInfo,
              impactValueNumber,
              confidenceValue: parseFloat(
                compatibleData.confidenceValues[rowIndex][columnIndex],
              ),
              cellValue: parseFloat(cell),
              sampleSize:
                getSampleSizeFromColumnName({ columnIndex, rowIndex }) ?? 0,
            })
          : null,
      };
    },
    [
      compatibleData,
      headerVar,
      getMetricAndFocusAreaFromColumnName,
      getSampleSizeFromColumnName,
    ],
  );

  const body = useMemo(() => {
    if (!compatibleData) return [];

    return compatibleData.rows.map((row, rowIndex) => ({
      key: row[0],
      cells: row
        .map((cell, columnIndex) =>
          buildCellData({ cell, columnIndex, rowIndex }),
        )
        .filter((cell) => cell !== null),
    }));
  }, [compatibleData, buildCellData]);

  const tooltipItemsForGrid = useMemo(() => {
    const headerItems = !showNewExplanations
      ? []
      : compatibleData.columnHeaders.flatMap((headerCell) => {
          const metricDefinition = getMetricByColumnHeaderValueFromObservation({
            columnHeaderValue: headerCell,
            myObservation,
          });

          if (!metricDefinition?.description) {
            return [];
          }

          const focusArea = getFocusAreaByMetric({
            metric: metricDefinition.columnHeaderValue,
          });

          if (!focusArea) {
            return [];
          }

          return [
            {
              key: metricDefinition.columnHeaderValue,
              cell: {
                type: 'heatmap-column-header' as const,
                metricDescription: metricDefinition.description,
              },
            },
          ];
        });

    const bodyItems = body.flatMap((row) =>
      row.cells
        .map((cell) => {
          if ('tooltipItem' in cell && cell.tooltipItem !== null) {
            return {
              key: cell.key,
              cell: cell.tooltipItem,
            };
          }
          return null;
        })
        .filter((item) => item !== null),
    );

    return [...headerItems, ...bodyItems];
  }, [
    body,
    compatibleData.columnHeaders,
    getFocusAreaByMetric,
    myObservation,
    showNewExplanations,
  ]);

  const { userAuthenticatedApiCall } = useUser();

  const isEmbedMode = useEmbedMode();

  // Navigate to deep dive logic
  const getDeepDiveLink = useCallback(
    async (cell: HeatmapTableCell) => {
      if (
        !myObservation ||
        !lastSuccessfulCustomVizRequest ||
        lastSuccessfulCustomVizRequest.customVizId !== 'heatmap' ||
        isEmbedMode
      ) {
        return;
      }

      const { group, focusArea, metric } = cell;

      const filtersWithGroupSingleQuery =
        lastSuccessfulCustomVizRequest.inputs.extras.filters.map((filter) => {
          return {
            ...filter,
            values:
              filter.columnHeaderValue ===
              viewConfiguration?.selectedFilterDefinition
                ? [group]
                : filter.values,
          };
        });

      const { dimension, treatmentDimensionLevel } =
        lastSuccessfulCustomVizRequest.inputs.extras;

      const request: BiasAnalysisRequest = {
        inputs: {
          dropdowns: {},
          extras: {
            metric,
            dimension,
            focusArea,
            filters: filtersWithGroupSingleQuery,
            treatmentDimensionLevel,
          },
        },
        customVizId: 'biasAnalysis',
        organizationId: lastSuccessfulCustomVizRequest.organizationId,
        observationLocationType: myObservation.type,
      };

      const shareLinkResult = await callGenerateShareLink({
        windowUrl: window.location.origin,
        customVizRequest: request,
        userAuthenticatedApiCall,
      });
      if (!shareLinkResult) return;

      return shareLinkResult.shareLink;
    },
    [
      myObservation,
      lastSuccessfulCustomVizRequest,
      isEmbedMode,
      userAuthenticatedApiCall,
      viewConfiguration?.selectedFilterDefinition,
    ],
  );

  const tooltipProps = useTouchableTooltipCollection({
    items: tooltipItemsForGrid,
    tooltipFn: (item) => {
      if (item.cell.type === 'heatmap-cell') {
        return (
          <TableGridTooltip
            getDeepDiveLink={getDeepDiveLink}
            cell={item.cell}
          />
        );
      }

      if (showNewExplanations && item.cell.type === 'heatmap-column-header') {
        return (
          <TableGridColumnHeaderTooltip
            metricDescription={item.cell.metricDescription}
          />
        );
      }

      return <></>;
    },
  });

  return (
    <TableGrid
      backgroundColorClass={'bg-white'}
      hideSearch={false}
      shouldScroll={true}
      header={headerVar}
      body={body}
      tooltipProps={tooltipProps}
    />
  );
};
